import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Building2, Users, Globe2, Timer, UserCheck, Receipt, FileText, SearchCheck, FileBadge, Truck } from "lucide-react";
import { Seo, organizationLd } from "@/components/Seo";
import { LineReveal, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CtaBand } from "@/components/CtaBand";
import { site } from "@/config/site";
import { services } from "@/data/services";

/* Image Unsplash (libre de droits) — remplacer par un visuel propriétaire si souhaité. */
const HERO_IMG = "https://images.unsplash.com/photo-1759826350352-c5b0b77729bd?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";
const ABOUT_IMG = "https://images.unsplash.com/photo-1773126378915-793b5c48fb38?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

const reassurance = [
  { icon: Building2, t: "Société immatriculée", d: `RCS de Bobigny — ${site.rcsNumber}` },
  { icon: Users, t: "Relation B2B", d: "Activité interentreprises exclusivement" },
  { icon: Globe2, t: "Sourcing France & international", d: "Fournisseurs identifiés et qualifiés" },
  { icon: Timer, t: `Devis sous ${site.quoteDelay}`, d: "Proposition chiffrée personnalisée" },
  { icon: UserCheck, t: "Interlocuteur unique", d: "Un contact direct et réactif" },
  { icon: Receipt, t: "Facturation professionnelle", d: "En euros, hors taxes, TVA en sus" },
];

const steps = [
  { icon: FileText, t: "Vous décrivez votre besoin", d: "Via le formulaire de devis : produits, volumes, délais, contraintes." },
  { icon: SearchCheck, t: "Nous analysons et sourçons", d: "Identification des fournisseurs, comparaison des offres, contrôle des spécifications." },
  { icon: FileBadge, t: "Vous recevez une proposition chiffrée", d: "Un devis clair, en euros HT, précisant les modalités au cas par cas." },
  { icon: Truck, t: "Nous organisons l'approvisionnement", d: "Coordination transport, suivi des commandes et reporting jusqu'à la livraison." },
];

const Hero = () => {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, reduce ? 0 : 120]);
  const scale = useTransform(scrollY, [0, 800], [1, reduce ? 1 : 1.08]);
  return (
    <section className="relative min-h-[100svh] overflow-hidden bg-night text-white grain" data-testid="hero-section">
      <div className="absolute inset-0 grid-lines" aria-hidden="true" />
      <div className="container-x relative grid min-h-[100svh] items-center gap-12 pb-20 pt-32 lg:grid-cols-12 lg:pb-28 lg:pt-36">
        <div className="lg:col-span-7">
          <motion.p className="eyebrow" initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}>
            {site.legalFormShort} · Pantin (93) · France & international
          </motion.p>
          <h1 className="h-display mt-6" data-testid="hero-title">
            <LineReveal lines={["MINES DE PARIS —", "Négoce et commerce", "de gros de produits", "non alimentaires"]} delay={0.25} />
          </h1>
          <Reveal delay={0.75} y={16}>
            <p className="lede mt-7 max-w-xl !text-white/70">{site.tagline}</p>
          </Reveal>
          <Reveal delay={0.9} y={16} className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact#devis" className="btn-gold !px-7 !py-4 text-base" data-testid="hero-devis-btn">
              Demander un devis <ArrowUpRight className="h-5 w-5" />
            </Link>
            <Link to="/services" className="btn-outline-light !px-7 !py-4 text-base" data-testid="hero-services-btn">
              Découvrir nos services <ArrowRight className="h-5 w-5" />
            </Link>
          </Reveal>
        </div>
        <motion.div
          className="relative lg:col-span-5"
          initial={reduce ? false : { opacity: 0, clipPath: "inset(0 0 100% 0)" }}
          animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.3, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="absolute -left-5 -top-5 h-full w-full rounded-2xl border border-gold/50" aria-hidden="true" />
          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl">
            <motion.img
              src={HERO_IMG}
              alt="Terminal logistique avec conteneurs — illustration du négoce international"
              width={1120}
              height={1400}
              className="h-full w-full object-cover"
              style={{ y, scale }}
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-night/70 via-transparent to-transparent" aria-hidden="true" />
            <div className="absolute bottom-5 left-5 right-5 rounded-lg border border-white/15 bg-night/70 p-4 backdrop-blur-md">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold">Devis personnalisé</p>
              <p className="mt-1 font-heading text-lg font-bold">Sous {site.quoteDelay}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Seo
        title="MINES DE PARIS — Commerce de gros et négoce de produits non alimentaires"
        description="MINES DE PARIS, SASU basée à Pantin (93) : commerce de gros de produits non alimentaires, services interentreprises de négoce, sourcing et approvisionnement B2B en France et à l'international. Devis sous 48 h ouvrées."
        path="/"
        jsonLd={[organizationLd]}
      />
      <Hero />
      <Marquee />

      {/* Réassurance */}
      <section className="bg-mist py-16 sm:py-24" data-testid="reassurance-section">
        <div className="container-x">
          <div className="grid gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
            {reassurance.map((r, i) => (
              <Reveal key={r.t} delay={i * 0.06} className="group bg-white p-7 transition-colors duration-300 hover:bg-night">
                <r.icon className="h-6 w-6 text-gold" aria-hidden="true" />
                <h2 className="mt-5 font-heading text-base font-bold text-night transition-colors group-hover:text-white md:text-lg">{r.t}</h2>
                <p className="mt-1.5 text-sm text-muted transition-colors group-hover:text-white/70">{r.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Présentation */}
      <section className="py-20 sm:py-28" data-testid="about-section">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -bottom-5 -right-5 h-full w-full rounded-2xl bg-mist" aria-hidden="true" />
              <img src={ABOUT_IMG} alt="Conteneurs empilés sur un terminal portuaire" width={1200} height={900} loading="lazy" className="relative aspect-[4/3] w-full rounded-2xl object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="eyebrow">Qui nous sommes</p>
              <h2 className="h-section mt-5 text-night">Une structure dédiée au négoce interentreprises</h2>
            </Reveal>
            <div className="mt-8 space-y-5 text-base leading-relaxed text-ink">
              <Reveal delay={0.05}><p><strong className="text-night">Qui nous sommes.</strong> {site.name} est une société par actions simplifiée unipersonnelle (SASU) au capital de {site.capital}, dont le siège est situé à {site.address.cityDisplay} ({site.address.postalCode}). La société est immatriculée au RCS de {site.rcsCity} depuis le {site.registrationDate} et a débuté son activité le {site.activityStart}.</p></Reveal>
              <Reveal delay={0.1}><p><strong className="text-night">Ce que nous faisons.</strong> Commerce de gros de produits non alimentaires et services interentreprises de négoce : achat-revente, sourcing, intermédiation commerciale et organisation de l'approvisionnement.</p></Reveal>
              <Reveal delay={0.15}><p><strong className="text-night">Notre approche.</strong> Réactivité, transparence, relation directe avec un interlocuteur unique et solutions sur mesure adaptées à chaque demande.</p></Reveal>
              <Reveal delay={0.2}><p><strong className="text-night">Notre périmètre.</strong> France métropolitaine et international, selon des modalités précisées au cas par cas dans le devis.</p></Reveal>
            </div>
            <Reveal delay={0.25} className="mt-8">
              <Link to="/a-propos" className="btn-outline-dark" data-testid="home-about-link">En savoir plus sur la société <ArrowRight className="h-4 w-4" /></Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-night py-20 text-white sm:py-28" data-testid="services-overview-section">
        <div className="container-x">
          <Reveal className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="eyebrow">Nos services</p>
              <h2 className="h-section mt-5">Du sourcing à la livraison, <span className="text-gold">un seul interlocuteur</span></h2>
            </div>
            <Link to="/services" className="btn-outline-light shrink-0" data-testid="home-services-link">Tous les services <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {services.slice(0, 4).map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.08}>
                <Link to={`/services#${s.slug}`} className="group flex h-full flex-col rounded-xl border border-white/10 bg-white/[0.03] p-7 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-1 hover:border-gold/60 hover:bg-white/[0.06]" data-testid={`home-service-card-${s.slug}`}>
                  <div className="flex items-center justify-between">
                    <s.icon className="h-7 w-7 text-gold" aria-hidden="true" />
                    <span className="font-heading text-sm font-bold text-white/30">{s.num}</span>
                  </div>
                  <h3 className="mt-8 font-heading text-lg font-bold leading-snug">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-white/60">{s.short}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold">Découvrir <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Méthode */}
      <section className="py-20 sm:py-28" data-testid="method-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Notre méthode</p>
            <h2 className="h-section mt-5 max-w-2xl text-night">Quatre étapes, un déroulé transparent</h2>
          </Reveal>
          <ol className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <Reveal as="li" key={s.t} delay={i * 0.1} className="relative border-t-2 border-line pt-7">
                <span className="absolute -top-0.5 left-0 h-0.5 w-16 bg-gold" aria-hidden="true" />
                <span className="font-heading text-5xl font-extrabold text-night/10">0{i + 1}</span>
                <s.icon className="mt-4 h-6 w-6 text-slate" aria-hidden="true" />
                <h3 className="mt-4 font-heading text-lg font-bold text-night">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted">{s.d}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Engagements */}
      <section className="bg-mist py-20 sm:py-28" data-testid="commitments-section">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="eyebrow">Nos engagements</p>
            <h2 className="h-section mt-5 text-night">Des engagements concrets, sans promesses excessives</h2>
            <p className="lede mt-5">Nous préférons des engagements honnêtes et tenus à des chiffres impressionnants.</p>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:col-span-8">
            {[
              { k: site.quoteDelay, t: "pour recevoir un devis", d: "lorsque la demande est complète" },
              { k: site.responseDelay, t: "pour une première réponse", d: "à toute demande reçue via le site" },
              { k: "1 interlocuteur", t: "unique et identifié", d: "du premier échange à la livraison" },
              { k: "100 % B2B", t: "relation interentreprises", d: "facturation professionnelle en euros" },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08} className="card p-7">
                <p className="font-heading text-3xl font-extrabold text-night sm:text-4xl">{c.k}</p>
                <p className="mt-2 font-semibold text-slate">{c.t}</p>
                <p className="mt-1 text-sm text-muted">{c.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/*
        BLOC « ILS NOUS FONT CONFIANCE » — DÉSACTIVÉ VOLONTAIREMENT.
        Aucun témoignage client n'est affiché tant que de vrais retours clients (nom, société, autorisation écrite)
        ne sont pas disponibles. Pour l'activer : créer un composant Testimonials avec les témoignages réels
        et l'insérer ici, entre « Nos engagements » et l'appel à l'action final.
      */}

      <CtaBand />
    </>
  );
}
